import React, { useState } from 'react';
import { recallUpsellToken } from '../../../../../../../utils/upsellToken';
import {
  Typography,
  Button,
  Box,
  CircularProgress,
  Chip,
  Paper,
  Divider
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StarsIcon from '@mui/icons-material/Stars';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { useShowSnackbar } from '../../../../../../../utils/snackbar';
import { useNetwork } from '../../../../../../../utils/network';

interface GiveawayExclusiveOfferProps {
  paymentIntentId: any;
  packageWithUpsell: {
    id: string;
    cards: number;
    entries: number;
    Upsell: {
      price: string;
      extra_cards: number;
      extra_entries: number;
    };
  };
  handleClose: () => void;
}

const glassCardSx = {
  borderRadius: 4,
  border: '1px solid rgba(18, 31, 38, 0.10)',
  background: '#ffffff',
  boxShadow: '0 24px 64px rgba(18, 31, 38, 0.10)',
  overflow: 'hidden'
};

const GiveawayExclusiveOffer: React.FC<GiveawayExclusiveOfferProps> = ({
  paymentIntentId,
  packageWithUpsell,
  handleClose
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { showSnackbar } = useShowSnackbar();
  const { post } = useNetwork();

  const [loading, setLoading] = useState(false);

  const extraEntries = Number(packageWithUpsell?.Upsell?.extra_entries ?? 0);
  const extraNfts = Number(packageWithUpsell?.Upsell?.extra_cards ?? 0);
  const price = String(packageWithUpsell?.Upsell?.price ?? '0');

  const handleYes = async () => {
    if (!stripe || !elements) {
      showSnackbar('Stripe has failed to load.', 'error');
      return;
    }

    setLoading(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        showSnackbar('Failed to submit payment details.', 'error');
        setLoading(false);
        return;
      }

      const result = await post(
        `/checkout/upsell-payment-intent`,
        // the charge is authorised by the token, not by knowing the intent id
        { upsellToken: recallUpsellToken(paymentIntentId) },
        true
      );

      const { error } = await stripe.confirmPayment({
        clientSecret: result.clientSecret,
        confirmParams: { return_url: `${window.location}` },
        redirect: 'if_required'
      });

      if (error) {
        showSnackbar('An unexpected error occurred.', 'error');
      } else {
        showSnackbar('Payment successful!', 'success');
        sessionStorage.setItem(
          'giveawayUpsellApplied',
          JSON.stringify({
            extraEntries,
            extraNfts,
            price: Number(price ?? 0)
          })
        );
        handleClose();
      }
    } catch (e) {
      showSnackbar('An unexpected error occurred.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ textAlign: 'center', color: '#111827' }}>
      {/* Premium header */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 0.9,
            borderRadius: 999,
            border: '1px solid rgba(36, 191, 189, 0.25)',
            background: 'rgba(36, 191, 189, 0.10)'
          }}
        >
          <LocalOfferIcon
            sx={{ fontSize: 18, color: '#0891b2' }}
          />
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 950,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: '#0891b2'
            }}
          >
            One-time Exclusive Offer
          </Typography>
        </Box>

        <Typography
            sx={{
              mt: 2.2,
              fontSize: { xs: 34, sm: 44, md: 54 },
              fontWeight: 1000,
              letterSpacing: '-0.035em',
              lineHeight: 1.05,
              color: '#111827'
            }}
          >
          Upgrade your entry
        </Typography>

        <Typography
          sx={{
            mt: 1.2,
            fontSize: { xs: 16, md: 18 },
            fontWeight: 750,
            color: 'rgba(17,24,39,0.68)',
            maxWidth: 700,
            mx: 'auto'
          }}
        >
          You’ve already secured your entry — add this upgrade to increase your
          odds. This exclusive add-on is only available right now!
        </Typography>
      </Box>

      {/* Main offer card */}
      <Paper sx={{ ...glassCardSx, p: { xs: 2.5, md: 3.5 } }}>
        {/* Top row: chips */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 1,
            flexWrap: 'wrap',
            mb: 2
          }}
        >
          <Chip
            icon={<BoltIcon />}
            label="Limited-time add-on"
            sx={{
              fontWeight: 900,
              color: '#111827',
              background: '#f8fafc',
              border: '1px solid rgba(18,31,38,0.10)'
            }}
          />
          <Chip
            icon={<TrendingUpIcon />}
            label="Higher odds"
            sx={{
              fontWeight: 900,
              color: '#111827',
              background: '#f8fafc',
              border: '1px solid rgba(18,31,38,0.10)'
            }}
          />
          <Chip
            icon={<StarsIcon />}
            label="Best value"
            sx={{
              fontWeight: 900,
              color: 'rgba(0,0,0,0.85)',
              background: 'rgba(124,255,250,0.85)'
            }}
          />
        </Box>

        {/* Big numbers */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mt: 1.5
          }}
        >
          <Box
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(18,31,38,0.10)',
              background: '#f8fafc',
              p: 2.2,
              textAlign: 'left'
            }}
          >
            <Typography
              sx={{ color: 'rgba(17,24,39,0.60)', fontWeight: 800 }}
            >
              Extra Entries
            </Typography>
            <Typography
              sx={{
                mt: 0.6,
                fontSize: { xs: 34, md: 40 },
                fontWeight: 1000,
                letterSpacing: '-0.03em',
                color: '#111827'
              }}
            >
              +{extraEntries}
            </Typography>
            <Typography
              sx={{ mt: 0.4, color: 'rgba(17,24,39,0.60)', fontWeight: 700 }}
            >
              More entries = more chances to win.
            </Typography>
          </Box>

          <Box
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(18,31,38,0.10)',
              background: '#f8fafc',
              p: 2.2,
              textAlign: 'left'
            }}
          >
            <Typography
              sx={{ color: 'rgba(17,24,39,0.60)', fontWeight: 800 }}
            >
              Bonus Digital Collectibles
            </Typography>
            <Typography
              sx={{
                mt: 0.6,
                fontSize: { xs: 34, md: 40 },
                fontWeight: 1000,
                letterSpacing: '-0.03em',
                color: '#111827'
              }}
            >
              +{extraNfts}
            </Typography>
            <Typography
              sx={{ mt: 0.4, color: 'rgba(17,24,39,0.60)', fontWeight: 700 }}
            >
              More collectibles added to your rewards.
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2.5, borderColor: 'rgba(18,31,38,0.08)' }} />

        {/* Price + CTA */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.4
          }}
        >
          <Box
            sx={{
              px: 2.2,
              py: 1,
              borderRadius: 999,
              border: '1px solid rgba(36, 191, 189, 0.25)',
              background: 'rgba(36, 191, 189, 0.10)'
            }}
          >
            <Typography sx={{ fontWeight: 950, color: '#111827' }}>
              Add this upgrade for{' '}
              <span style={{ color: '#0891b2' }}>A${price}</span>
            </Typography>
          </Box>

          <Typography
            sx={{
              color: 'rgba(17,24,39,0.50)',
              fontWeight: 700,
              fontSize: 13
            }}
          >
            This offer is only available right now.
          </Typography>

          <Box
            sx={{
              mt: 0.6,
              display: 'flex',
              gap: 1.2,
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}
          >
            <Button
              onClick={handleYes}
              disabled={loading}
              sx={{
                height: 48,
                px: 3.5,
                borderRadius: 999,
                fontWeight: 1000,
                color: 'rgba(0,0,0,0.85)',
                background:
                  'linear-gradient(90deg, rgba(124,255,250,0.35) 0%, rgba(124,255,250,0.95) 100%)',
                border: '1px solid rgba(124,255,250,0.25)',
                minWidth: 180
              }}
            >
              {loading ? <CircularProgress size={22} /> : 'Yes, boost my entry'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                height: 48,
                px: 3.5,
                borderRadius: 999,
                fontWeight: 900,
                borderColor: 'rgba(18,31,38,0.14)',
                color: '#111827',
                minWidth: 150
              }}
            >
              No thanks
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Subtle trust text */}
      <Typography
        sx={{
          mt: 2.5,
          color: 'rgba(17,24,39,0.48)',
          fontWeight: 700,
          fontSize: 12
        }}
      >
        Secure checkout powered by Stripe. No additional account setup required.
      </Typography>
    </Box>
  );
};

export default GiveawayExclusiveOffer;
